import InputGroup from '@shared/components/InputGroup/InputGroup';
import Switch from '@shared/components/Switch/Switch';
import { defaultHoverTransition } from '@shared/utils';
import cx from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as BuildingCommunity2 } from 'assets/icons/BuildingCommunity2.svg';
import { ReactComponent as LinkNew } from 'assets/icons/LinkNew.svg';
import { ReactComponent as SearchIconComponent } from 'assets/icons/Search.svg';
import { ReactComponent as UserPlusIcon } from 'assets/icons/UserPlus.svg';
import useToast from 'contexts/ToastProvider/useToast';
import {
  isEmailFormat,
  isEmailOrMatrixIdFormat,
  isMatrixIdFormat,
  normalizeMatrixId,
} from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';

import InviteParticipantsField from './InviteParticipantsField';
import useWorkspaceMemberMatrixIds from './useWorkspaceMemberMatrixIds';

const COPIED_FEEDBACK_MS = 1500;

export type InviteInputMode = 'emailAndMatrixId' | 'matrixIdOnly';

export interface InviteParticipantsSectionProps {
  inviteInputMode: InviteInputMode;
  participants: string[];
  onParticipantsChange: (participants: string[]) => void;
  meetingUrl?: string;
  showOrganisationMeetingToggle?: boolean;
  isOrganisationMeeting?: boolean;
  onOrganisationMeetingChange?: (value: boolean) => void;
  /** Matrix room whose joined/invited members should not appear in the picker (invite-more flows). */
  excludeRoomId?: string;
}

const InviteParticipantsSection = ({
  inviteInputMode,
  participants,
  onParticipantsChange,
  meetingUrl,
  showOrganisationMeetingToggle = false,
  isOrganisationMeeting = false,
  onOrganisationMeetingChange,
  excludeRoomId,
}: InviteParticipantsSectionProps) => {
  const { t } = useTranslation();
  const { workspaceId } = useWorkspacesContext();
  const { pushToast } = useToast();
  const workspaceMemberIds = useWorkspaceMemberMatrixIds();
  const [inputValue, setInputValue] = useState('');
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');
  const [displayNameMap, setDisplayNameMap] = useState<Record<string, string>>({});

  const normalizedInput = normalizeMatrixId(inputValue.trim());
  const isMatrixOnly = inviteInputMode === 'matrixIdOnly';
  const inputMatchesFormat = isMatrixOnly
    ? isMatrixIdFormat(normalizedInput)
    : isEmailOrMatrixIdFormat(normalizedInput);
  const shouldShowDropdown = !!inputValue.trim() && inputMatchesFormat;

  const handleCopyLink = () => {
    if (!meetingUrl) return;
    void navigator.clipboard.writeText(meetingUrl);
    setCopyState('copied');
    setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);
  };

  const handleDropdownAdd = () => {
    if (!inputMatchesFormat) return;
    const finalValue = isEmailFormat(normalizedInput) ? normalizedInput.toLowerCase() : normalizedInput;
    if (!finalValue || participants.includes(finalValue)) return;

    // #8018 — in organisation meetings only workspace members may be added via typed Matrix ID.
    // Block non-members with a toast and keep the input so the user can correct it.
    if (isOrganisationMeeting && isMatrixIdFormat(finalValue) && !workspaceMemberIds.has(finalValue.toLowerCase())) {
      pushToast({ variant: 'info', title: t('schedule_meeting.org_meeting_external_not_allowed') });
      return;
    }

    onParticipantsChange([...participants, finalValue]);
    setInputValue('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && shouldShowDropdown) {
      e.preventDefault();
      handleDropdownAdd();
    }
  };

  // #8018 / BUG-004 — enabling an organisation meeting must also drop participants added beforehand
  // that aren't workspace members (emails and non-member Matrix IDs), mirroring the add-time guard.
  const handleOrganisationMeetingChange = (value: boolean) => {
    if (value) {
      const memberParticipants = participants.filter(
        (id) => isMatrixIdFormat(id) && workspaceMemberIds.has(id.toLowerCase())
      );
      if (memberParticipants.length !== participants.length) {
        onParticipantsChange(memberParticipants);
        pushToast({ variant: 'info', title: t('schedule_meeting.org_meeting_external_not_allowed') });
      }
    }
    onOrganisationMeetingChange?.(value);
  };

  const handleToggle = useCallback(
    (userId: string, displayName?: string) => {
      if (participants.includes(userId)) {
        onParticipantsChange(participants.filter((id) => id !== userId));
        return;
      }
      if (displayName) {
        setDisplayNameMap((prev) => ({ ...prev, [userId]: displayName }));
      }
      onParticipantsChange([...participants, userId]);
    },
    [onParticipantsChange, participants]
  );

  const hintKey = isOrganisationMeeting
    ? 'schedule_meeting.organisation_meeting_hint'
    : isMatrixOnly
    ? 'schedule_meeting.add_by_matrix_hint'
    : 'schedule_meeting.add_by_email_or_matrix_hint';

  const placeholderKey = isMatrixOnly
    ? 'schedule_meeting.search_and_add_matrix_placeholder'
    : 'schedule_meeting.search_and_add_placeholder';

  return (
    <>
      {showOrganisationMeetingToggle && workspaceId && (
        <>
          <div className="flex gap-2">
            <div className="md:px-3">
              <BuildingCommunity2 className="h-6 w-6 shrink-0 stroke-1 stroke-gray-60" />
            </div>
            <div className="flex flex-1 items-start gap-6">
              <div className="flex flex-1 flex-col">
                <span className="text-size-base text-black">{t('schedule_meeting.organisation_meeting_label')}</span>
                <span className="text-size-sm text-gray-60">
                  {t('schedule_meeting.organisation_meeting_description')}
                </span>
              </div>
              <Switch checked={!!isOrganisationMeeting} onChange={handleOrganisationMeetingChange} />
            </div>
          </div>
          <hr className="border-gray-20" />
        </>
      )}

      <div className="flex gap-2">
        <div className="md:px-3">
          <UserPlusIcon className="h-6 w-6 shrink-0 stroke-1.5 stroke-gray-60" />
        </div>
        <div className="flex flex-1 min-w-0 flex-col gap-2">
          <div className="flex h-6 items-center justify-between">
            <span className="text-size-base font-medium text-black">
              {t('schedule_meeting.invite_participants_label')}
            </span>
            {meetingUrl && (
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex shrink-0 items-center gap-1 text-primary-50 transition-colors"
                aria-label={t('schedule_meeting.copy_link_button')}
              >
                <LinkNew className="h-4 w-4 stroke-current stroke-1.5" />
                <span className="text-size-xs font-medium">
                  {copyState === 'copied'
                    ? t('schedule_meeting.copy_link_copied')
                    : t('schedule_meeting.copy_link_button')}
                </span>
              </button>
            )}
          </div>

          <div className="relative">
            <InputGroup variant="white-bg">
              <InputGroup.Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t(placeholderKey)}
                onKeyDown={handleSearchKeyDown}
                secondaryIcon={SearchIconComponent}
                secondaryIconClassName="stroke-gray-100 stroke-current"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                data-testid="search-and-add-input"
              />
            </InputGroup>
            {shouldShowDropdown && (
              <div className="absolute border w-full bg-white top-13 rounded-xl p-2 shadow-dropdown z-10">
                <div
                  className={cx(
                    'w-full cursor-pointer hover:bg-primary-10 rounded-lg p-2 text-black text-size-sm truncate',
                    defaultHoverTransition
                  )}
                  onClick={handleDropdownAdd}
                  data-testid="search-and-add-dropdown"
                >
                  <span>{normalizedInput}</span>
                </div>
              </div>
            )}
          </div>
          <span className="text-size-sm text-black">{t(hintKey)}</span>
          <InviteParticipantsField
            workspaceId={workspaceId}
            searchTerm={inputValue}
            participants={participants}
            onToggle={handleToggle}
            displayNameMap={displayNameMap}
            inviteInputMode={inviteInputMode}
            excludeRoomId={excludeRoomId}
          />
        </div>
      </div>
    </>
  );
};

export default InviteParticipantsSection;
